<template>
  <div class="form-container mt-3" id="print">
    <div class="wrapper">
      <fields :detail="detail" />
    </div>
    <b-table
      class="mt-2"
      bordered
      thead-class="bg-color text-center"
      empty-text="No data"
      :items="detail.HmEwhTable"
      :fields="fields"
      responsive
      show-empty
    >
      <template #thead-top>
        <b-tr>
          <b-th></b-th>
          <b-th>
            <b-form-checkbox
              v-if="showCheck()"
              :checked="getCheckAllValue()"
              @change="onCheckAll($event)"
            ></b-form-checkbox>
          </b-th>
          <b-th></b-th>
          <b-th></b-th>
          <b-th colspan="3">Hour Meter</b-th>
          <b-th colspan="4">Operation Hours</b-th>
          <b-th colspan="3">Available Hours</b-th>
          <b-th colspan="4">Delay Hours</b-th>
          <b-th></b-th>
          <b-th></b-th>
          <b-th></b-th>
          <b-th></b-th>
        </b-tr>
      </template>
      <template v-slot:cell(selected)="items">
        <b-form-checkbox
          v-if="showCheck(items.item)"
          v-model="items.item.check"
        ></b-form-checkbox>
      </template>
      <template v-slot:cell(detail)="{ item }">
        <a
          :href="`/log-sheet/${item.id}/detail`"
        >
          See Logsheet
        </a>
      </template>
      <template v-slot:cell(hm_start)="items">
        {{ items.item.hours_meter.start.toFixed(2) }}
      </template>
      <template v-slot:cell(hm_finish)="items">
        {{ items.item.hours_meter.finish.toFixed(2) }}
      </template>
      <template v-slot:cell(hm_total)="items">
        {{ items.item.hours_meter.total.toFixed(2) }}
      </template>
      <template v-slot:cell(oh_start)="items">
        {{ formatTime(items.item.operational_hours.start) }}
      </template>
      <template v-slot:cell(oh_rest)="items">
        {{ formatTime(items.item.operational_hours.rest) }}
      </template>
      <template v-slot:cell(oh_finish)="items">
        {{ formatTime(items.item.operational_hours.finish) }}
      </template>
      <template v-slot:cell(oh_total)="items">
        {{ formatPrice(items.item.operational_hours.total) }}
      </template>
      <template v-slot:cell(ah_start)="items">
        {{ formatTime(items.item.available_hours.start) }}
      </template>
      <template v-slot:cell(ah_finish)="items">
        {{ formatTime(items.item.available_hours.finish) }}
      </template>
      <template v-slot:cell(ah_total)="items">
        {{ formatPrice(items.item.available_hours.total) }}
      </template>
      <template v-slot:cell(delay_hours_standby)="items">
        {{ items.item.delay_hours.standby.toFixed(2) }}
      </template>
      <template v-slot:cell(delay_hours_breakdown)="items">
        {{ items.item.delay_hours.breakdown.toFixed(2) }}
      </template>
      <template v-slot:cell(delay_hours_technical)="items">
        {{ items.item.delay_hours.technical.toFixed(2) }}
      </template>
      <template v-slot:cell(delay_hours_total)="items">
        {{ items.item.delay_hours.total.toFixed(2) }}
      </template>
      <template v-slot:cell(working_hour)="items">
        {{ items.item.working_hour.toFixed(2) }}
      </template>
      <template #cell(status)="items">
        <div class="px-2" :class="statusChange(items.item.status)">{{ items.item.status }}</div>
      </template>
      <template v-slot:custom-foot>
        <tr class="bg-color text-center">
          <td></td>
          <td></td>
          <td>{{ summary.totalDate }} Days</td>
          <td>{{ summary.totalShift }} Shifts</td>
          <td colspan="3">HM {{ parseFloat(summary.totalHoursMeter).toFixed(2) }} Hours</td>
          <td colspan="4">
            Operation {{ formatPrice(summary.totalOperationHour) }} Hours
          </td>
          <td colspan="3">
            Available {{ formatPrice(summary.totalAvailableHour) }} Hours
          </td>
          <td colspan="4">Delay {{ parseFloat(summary.totalDelayHour).toFixed(2) }} Hours</td>
          <td>{{ parseFloat(summary.totalWorkingHour).toFixed(2) }} Hours</td>
          <td>{{ summary.totalActivity }} Activity</td>
          <td>{{ summary.totalOperator }} Person</td>
          <td></td>
        </tr>
      </template>
    </b-table>
    <div class="d-flex mt-5">
      <div id="history">
        <b-button
          ref="button"
          class="btn-custom mr-2"
          variant="info"
          size="sm"
          @click="isHidden = !isHidden"
        >
         Log History
        </b-button>
        <b-card
          class="log-history text-center"
          v-show="isHidden"
          v-closable="{
            exclude: ['button'],
            handler: 'onClose'
          }"
        >
          <div class="timeline small">
            <div v-if="detail.approval_history && detail.approval_history.length > 0">
              <ul class="list-history">
                <li
                  class="list-history-child"
                  v-for="(info, index) in detail.approval_history"
                  v-bind:key="index">
                  <div class="timeline-log timeline-name">
                    {{ info.fullname }} - {{ info.event }}
                  </div>
                  <div class="timeline-log timeline-status">
                    {{ info.state }}
                  </div>
                  <div class="timeline-log">
                    {{ info.comment }}
                  </div>
                  <div class="timeline-log timeline-date mb-3">
                    {{ `${formatDate(info.created_at)}` }}
                  </div>
                </li>
              </ul>
            </div>
            <div class="no-history" v-else>
              <h5>There is no data history in here.</h5>
            </div>
          </div>
        </b-card>
      </div>
      <div class="d-flex flex-fill">
        <b-button
          class="btn-reset mr-2"
          variant="outline-info"
          size="sm"
          @click="onClickBack"
        >
          Back
        </b-button>
        <b-button
          class="btn-custom mr-auto"
          variant="info"
          size="sm"
          @click="onPrint"
        >
          Export
        </b-button>
        <b-button
          v-if="canAdd && detail.status === 'Draft'"
          class="btn-custom ml-auto mr-2"
          variant="info"
          size="sm"
          @click="onSaveAsDraft"
          :disabled="isLoading"
        >
          Save as Draft
        </b-button>
        <b-button
          v-if="canAdd && (detail.status === 'Draft' || detail.status === 'Revise')"
          class="btn-custom"
          variant="info"
          size="sm"
          @click="onSubmit"
          :disabled="isLoading"
        >
          Submit
        </b-button>
        <b-button
          v-if="detail.approval_status.isActiveRevise"
          class="btn-revise-cstm mr-2 ml-auto"
          variant="danger"
          size="sm"
          @click="onRevise"
          :disabled="isLoading"
        >
          Revise
        </b-button>
        <b-button
          v-if="detail.approval_status.isActiveApprove"
          class="btn-custom"
          variant="info" size="sm"
          @click="onApprove"
          :disabled="isLoading"
        >
          Approve
        </b-button>
      </div>
    </div>
    <modal-approve @submit="showComment" :form="detail" :text="text" />
    <modal-comment @submit="updateStatus" :action="action" />
    <modal-confirm :text="text" @submit="submitSummaryHm" />
    <modal-successful :message="text" />
  </div>
</template>

<script>
/* eslint-disable camelcase */
import api from '@/api'
import Fields from './Field'
import moment from 'moment'
import ModalApprove from '@/partials/ModalApprove'
import ModalComment from '@/partials/ModalComment'
import ModalConfirm from './ModalConfirm'
import ModalSuccessful from '@/partials/ModalSuccessful'
import { hasPermission } from '@/utils/permissions'
import { HM_EWH_ADD } from '@/fixtures'
import { closeEvent } from '@/utils/closeEvent'

closeEvent()

export default {
  components: {
    Fields,
    ModalApprove,
    ModalComment,
    ModalConfirm,
    ModalSuccessful
  },

  data: () => ({
    hm: {
      id: null,
      woId: null,
      unitId: null
    },
    isNew: true,
    canAdd: hasPermission(HM_EWH_ADD),
    isHidden: false,
    form: {},
    fields: [
      { key: 'detail', label: 'Detail', sortable: false },
      { key: 'selected', label: '', sortable: false },
      { key: 'date', label: 'Date', tdClass: 'text-center' },
      { key: 'shift', label: 'Shift', tdClass: 'text-center' },
      { key: 'hm_start', label: 'Start', tdClass: 'text-center' },
      { key: 'hm_finish', label: 'Finish', tdClass: 'text-center' },
      { key: 'hm_total', label: 'Total', tdClass: 'text-center' },
      { key: 'oh_start', label: 'Start', tdClass: 'text-center' },
      { key: 'oh_rest', label: 'Rest', tdClass: 'text-center' },
      { key: 'oh_finish', label: 'Finish', tdClass: 'text-center' },
      { key: 'oh_total', label: 'Total', tdClass: 'text-center' },
      { key: 'ah_start', label: 'Start', tdClass: 'text-center' },
      { key: 'ah_finish', label: 'Finish', tdClass: 'text-center' },
      { key: 'ah_total', label: 'Total', tdClass: 'text-center' },
      { key: 'delay_hours_standby', label: 'STBY', tdClass: 'text-center' },
      { key: 'delay_hours_breakdown', label: 'BD', tdClass: 'text-center' },
      { key: 'delay_hours_technical', label: 'Tech', tdClass: 'text-center' },
      { key: 'delay_hours_total', label: 'Total', tdClass: 'text-center' },
      { key: 'working_hour', label: 'Effective Working Hours', tdClass: 'text-center' },
      { key: 'activity', label: 'Activity', tdClass: 'text-center' },
      { key: 'operator', label: 'Operator', tdClass: 'text-center' },
      { key: 'status', label: 'Status', tdClass: 'text-center' }
    ],
    detail: {
      status: null,
      approval_status: {
        isActiveRevise: false,
        isActiveApprove: false,
        isEdit: false
      }
    },
    summary: {},
    text: '',
    action: '',
    comment: '',
    isSubmit: false,
    isDraft: false,
    isLoading: false
  }),

  created() {
    if (this.$route.query.hm_id && this.$route.query.hm_id > 0) {
      this.hm.id = this.$route.query.hm_id
    } else {
      this.isNew = false
    }
    if (this.$route.query.wo_id && this.$route.query.wo_id > 0) {
      this.hm.woId = this.$route.query.wo_id
    }
    if (this.$route.query.unit_id && this.$route.query.unit_id > 0) {
      this.hm.unitId = this.$route.query.unit_id
    }
    this.fetctDetailSummaryHM()
  },

  methods: {
    async fetctDetailSummaryHM() {
      const options = this.hm.id ? { id: this.hm.id } : { wo_id: this.hm.woId, unit_id: this.hm.unitId }
      const data = await api.summaryHM.get(options)
      data.HmEwhTable.map(d => {
        if (d.is_reference) {
          d._rowVariant = 'secondary'
        }
      })
      this.detail = data
      this.summary = this.detail.summary
    },
    onClickBack() {
      this.$router.push('/summary-hm-ewh')
    },
    onApprove() {
      this.text = 'Approve this summary hm ewh'
      this.action = 'approve'
      this.$bvModal.show('modal-approve')
    },
    onRevise() {
      this.text = 'Revise this summary hm ewh'
      this.action = 'revise'
      this.$bvModal.show('modal-approve')
    },
    showComment() {
      this.$bvModal.show('modal-comment')
    },
    onSaveAsDraft() {
      this.isSubmit = false
      this.isDraft = true
      this.text = 'save'
      this.$bvModal.show('modal-confirm')
    },
    onSubmit() {
      this.isSubmit = true
      this.isDraft = false
      this.text = 'submit'
      this.$bvModal.show('modal-confirm')
    },
    onPrint() {
      const url = `${process.env.VUE_APP_PRINT_URL}/SummaryHMEWH?WoId=${this.detail.wo_id}&UnitId=${this.detail.unit_id}&Id=${this.detail.id ?? 0}`
      window.open(url)
    },
    onClose() {
      this.isHidden = false
    },
    formatDate(date) {
      return moment(date).format('DD-MM-YYYY')
    },
    formatTime(time) {
      return moment(time, 'HH:mm:ss').format('HH:mm')
    },
    formatPrice(value, decLen = 2) {
      return value !== undefined ? Number(value.toFixed(decLen)).toLocaleString(undefined, { minimumFractionDigits: decLen, maximumFractionDigits: decLen }) : ''
    },
    async updateStatus(comment) {
      try {
        this.isLoading = true
        const checkStatus = this.action === 'approve'
          ? await api.summaryHM.approve(this.hm.id, comment)
          : await api.summaryHM.revise(this.hm.id, comment)
        if (checkStatus.data.error) {
          this.isLoading = false
          this.$nextTick(() => {
            this.$bvToast.toast(`${checkStatus.data.error.message}`, {
              headerClass: 'd-none',
              solid: true,
              variant: 'danger'
            })
          })
        } else {
          this.text = this.action === 'approve' ? 'Approve this hm-ewh' : 'Revise this hm-ewh'
          this.$bvModal.show('modal-successful')
          setTimeout(() => {
            this.$router.push('/summary-hm-ewh')
          }, 1500)
        }
      } catch ({ response }) {
        this.isLoading = false
        this.$nextTick(() => {
          this.$bvToast.toast(`${response.status} ${response.data.error.message}`, {
            headerClass: 'd-none',
            solid: true,
            variant: 'danger'
          })
        })
      }
    },
    async submitSummaryHm() {
      try {
        this.isLoading = true
        const ids = this.detail.HmEwhTable.filter(d => d.check).map(d => d.id)
        if (ids.length >= 1) {
          await api.summaryHM.submit({
            hm_id: this.hm.id ? parseInt(this.hm.id) : null,
            is_submit: this.isSubmit,
            is_draft: this.isDraft,
            logsheet_id: ids,
            wo_id: this.detail.wo_id,
            unit_id: this.detail.unit_id,
            company_id: this.detail.company_id
          })
          this.text = 'Submit this hm-ewh'
          this.$bvModal.show('modal-successful')
          setTimeout(() => {
            this.$router.push('/summary-hm-ewh')
          }, 2000)
        } else {
          this.isLoading = false
          this.$nextTick(() => {
            this.$bvToast.toast('This Summary HM/EWH must checklist first', {
              headerClass: 'd-none',
              solid: true,
              variant: 'danger'
            })
          })
        }
      } catch ({ response }) {
        this.isLoading = false
        this.$nextTick(() => {
          this.$bvToast.toast(`${response.status} ${response.data.error.message}`, {
            headerClass: 'd-none',
            solid: true,
            variant: 'danger'
          })
        })
      }
    },
    onCheckAll(value) {
      this.detail.HmEwhTable.map(d => {
        if (d.status === 'Approved' && !d.is_reference) {
          d.check = value
        }
      })
    },
    getCheckAllValue() {
      const totalData = this.detail.HmEwhTable.filter(d => d.status === 'Approved' && !d.is_reference).length
      const totalCheck = this.detail.HmEwhTable.filter(d => d.check && d.status === 'Approved' && !d.is_reference).length
      return totalCheck === totalData
    },
    statusChange(status) {
      if (status === 'Draft') {
        return 'draft'
      } else if (status === 'Approved') {
        return 'approved'
      } else if (status === 'Revise') {
        return 'revised'
      } else if (status === 'Waiting Approval') {
        return 'waiting-approval'
      } else {
        return 'waiting-approval'
      }
    },
    showCheck(item) {
      const isCanCheck = this.detail.status === 'Draft' || this.detail.approval_status.isEdit || this.detail.approval_status.isActiveRevise
      if (item) {
        return this.canAdd && isCanCheck && item.status === 'Approved' && item.can_select
      } else {
        return this.canAdd && isCanCheck
      }
    }
  },

  destroyed() {
    localStorage.removeItem('currentPage', this.currentPage)
    localStorage.removeItem('totalRows', this.totalRows)
  }
}
</script>

<style lang="scss">
.form-control {
  font-size: 12px;
}

.col-form-label,
.col-form-label-sm {
  font-size: 12px !important;
}

.table-secondary, .table-secondary > td {
  background-color: #f0f0f0;
  color: #b3b3b3;
}
</style>
